import { ChevronLeft, ChevronRight, Minus, Plus } from "lucide-react";
import * as Sentry from "@sentry/react";
import * as pdfjsLib from "pdfjs-dist";
import * as React from "react";
import { createRoot, type Root } from "react-dom/client";

import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { Button } from "@/components/ui";
import { cn } from "@/lib/utils/cn";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

type PDFJsViewerProps = {
  src: string;
  className?: string;
  contentClassName?: string;
  pageShellClassName?: string;
  showToolbar?: boolean;
  defaultZoomMultiplier?: number;
  onReady?: (api: { scrollToPage: (pageNumber: number) => void }) => void;
  renderPageOverlay?: (props: {
    pageHeight: number;
    pageNumber: number;
    pageWidth: number;
    scale: number;
  }) => React.ReactNode;
};

const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

function clampZoom(value: number) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2))));
}

function unmountOverlayRoot(root: Root) {
  // The viewer can unmount while its parent React root is committing.
  queueMicrotask(() => {
    root.unmount();
  });
}

export function PDFJsViewer({
  src,
  className,
  contentClassName,
  pageShellClassName,
  renderPageOverlay,
  showToolbar = true,
  defaultZoomMultiplier = 0.6,
  onReady,
}: PDFJsViewerProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const pageElementsRef = React.useRef<HTMLDivElement[]>([]);
  const overlayRootsRef = React.useRef<Map<number, Root>>(new Map());
  const pageMetaRef = React.useRef<
    Map<number, { pageHeight: number; pageWidth: number; scale: number }>
  >(new Map());
  const loadSequenceRef = React.useRef(0);
  const [status, setStatus] = React.useState<"loading" | "done" | "error">(
    "loading",
  );
  const [numPages, setNumPages] = React.useState(0);
  const [zoomMultiplier, setZoomMultiplier] = React.useState(
    defaultZoomMultiplier,
  );
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageInputValue, setPageInputValue] = React.useState("1");
  const renderPageOverlayRef = React.useRef(renderPageOverlay);

  React.useEffect(() => {
    renderPageOverlayRef.current = renderPageOverlay;
  }, [renderPageOverlay]);

  const scrollToPage = React.useCallback((pageNumber: number) => {
    const nextPage = Math.min(
      Math.max(pageNumber, 1),
      pageElementsRef.current.length || 1,
    );
    const target = pageElementsRef.current[nextPage - 1];
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setCurrentPage(nextPage);
    setPageInputValue(String(nextPage));
  }, []);

  React.useEffect(() => {
    onReady?.({ scrollToPage });
  }, [onReady, scrollToPage]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateCurrentPage = () => {
      const containerTop = container.getBoundingClientRect().top;
      let nextPage = 1;

      pageElementsRef.current.forEach((element, index) => {
        const { top } = element.getBoundingClientRect();
        if (top - containerTop <= 80) {
          nextPage = index + 1;
        }
      });

      setCurrentPage(nextPage);
      setPageInputValue(String(nextPage));
    };

    container.addEventListener("scroll", updateCurrentPage, { passive: true });
    updateCurrentPage();

    return () => {
      container.removeEventListener("scroll", updateCurrentPage);
    };
  }, [numPages]);

  React.useEffect(() => {
    const container = containerRef.current;
    const overlayRoots = overlayRootsRef.current;
    const pageMeta = pageMetaRef.current;
    if (!src || !container) return;

    const loadId = ++loadSequenceRef.current;
    let loadingTask: pdfjsLib.PDFDocumentLoadingTask | undefined;
    const fetchController = new AbortController();
    const fetchTimeoutId = window.setTimeout(() => {
      fetchController.abort(
        new DOMException("PDF download timed out", "TimeoutError"),
      );
    }, 45_000);
    setStatus("loading");

    void (async () => {
      try {
        const request = await fetch(src, { signal: fetchController.signal });
        if (!request.ok) throw new Error(`HTTP ${request.status}`);
        const data = new Uint8Array(await request.arrayBuffer());
        window.clearTimeout(fetchTimeoutId);

        loadingTask = pdfjsLib.getDocument({ data, enableXfa: true });
        const pdf = await Promise.race([
          loadingTask.promise,
          new Promise<never>((_, reject) => {
            setTimeout(() => {
              reject(new Error("PDF.js parse timeout"));
            }, 30_000);
          }),
        ]);
        if (loadSequenceRef.current !== loadId) return;

        container.innerHTML = "";
        pageElementsRef.current = [];
        setStatus("done");
        setNumPages(pdf.numPages);
        setCurrentPage(1);
        setPageInputValue("1");

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (loadSequenceRef.current !== loadId) return;

          const page = await pdf.getPage(pageNum);
          if (loadSequenceRef.current !== loadId) return;

          const naturalViewport = page.getViewport({ scale: 1 });
          const containerWidth = Math.max(container.clientWidth - 32, 320);
          const scale =
            (containerWidth / naturalViewport.width) * zoomMultiplier;
          const viewport = page.getViewport({ scale: Math.max(scale, 0.5) });

          const dpr = window.devicePixelRatio || 1;
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          canvas.style.display = "block";

          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          ctx.scale(dpr, dpr);

          const pageShell = document.createElement("div");
          pageShell.className =
            "mx-auto w-fit overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm";
          if (pageShellClassName) {
            pageShell.className = `${pageShell.className} ${pageShellClassName}`;
          }
          pageShell.style.position = "relative";
          pageElementsRef.current.push(pageShell);
          pageShell.appendChild(canvas);

          let overlayRoot: Root | undefined;
          const currentRenderPageOverlay = renderPageOverlayRef.current;
          if (currentRenderPageOverlay) {
            const overlay = document.createElement("div");
            overlay.style.position = "absolute";
            overlay.style.inset = "0";
            overlay.style.zIndex = "1";
            pageShell.appendChild(overlay);

            overlayRoot = createRoot(overlay);
            pageMeta.set(pageNum, {
              pageHeight: viewport.height,
              pageWidth: viewport.width,
              scale: viewport.scale,
            });
            overlayRoot.render(
              currentRenderPageOverlay({
                pageHeight: viewport.height,
                pageNumber: pageNum,
                pageWidth: viewport.width,
                scale: viewport.scale,
              }),
            );
            overlayRoots.set(pageNum, overlayRoot);
          }

          container.appendChild(pageShell);

          try {
            await page.render({ canvas, canvasContext: ctx, viewport }).promise;
          } catch (error) {
            pageShell.remove();
            if (overlayRoot && overlayRoots.get(pageNum) === overlayRoot) {
              overlayRoots.delete(pageNum);
              pageMeta.delete(pageNum);
              unmountOverlayRoot(overlayRoot);
            }
            throw error;
          }
        }
      } catch (error) {
        if (loadSequenceRef.current !== loadId) return;
        Sentry.captureException(error, {
          tags: { component: "PDFJsViewer" },
        });
        setStatus("error");
      } finally {
        window.clearTimeout(fetchTimeoutId);
      }
    })();

    return () => {
      loadSequenceRef.current += 1;
      window.clearTimeout(fetchTimeoutId);
      fetchController.abort();
      if (loadingTask) void loadingTask.destroy();
      overlayRoots.forEach((root) => {
        unmountOverlayRoot(root);
      });
      overlayRoots.clear();
      pageMeta.clear();
      pageElementsRef.current = [];
      container.innerHTML = "";
    };
  }, [pageShellClassName, src, zoomMultiplier]);

  React.useEffect(() => {
    if (!renderPageOverlay) return;

    overlayRootsRef.current.forEach((root, pageNumber) => {
      const meta = pageMetaRef.current.get(pageNumber);
      if (!meta) return;

      root.render(
        renderPageOverlay({
          pageHeight: meta.pageHeight,
          pageNumber,
          pageWidth: meta.pageWidth,
          scale: meta.scale,
        }),
      );
    });
  }, [renderPageOverlay]);

  return (
    <div
      className={cn(
        "bg-muted/30 relative flex h-full min-h-0 w-full flex-col overflow-hidden",
        className,
      )}
    >
      {showToolbar ? (
        <div className="bg-background flex min-h-12 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                scrollToPage(currentPage - 1);
              }}
              disabled={currentPage <= 1}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 text-sm">
              <input
                value={pageInputValue}
                onChange={(event) => {
                  setPageInputValue(event.target.value.replace(/\D/g, ""));
                }}
                onBlur={() => {
                  const parsedPage = Number(pageInputValue);
                  scrollToPage(
                    Number.isFinite(parsedPage) ? parsedPage : currentPage,
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  const parsedPage = Number(pageInputValue);
                  scrollToPage(
                    Number.isFinite(parsedPage) ? parsedPage : currentPage,
                  );
                }}
                className="border-input bg-background text-foreground h-8 w-14 rounded-md border px-2 text-center tabular-nums"
                inputMode="numeric"
                aria-label="Page number"
              />
              <span className="text-muted-foreground">of {numPages || 1}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                scrollToPage(currentPage + 1);
              }}
              disabled={currentPage >= numPages}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                setZoomMultiplier((current) => clampZoom(current - ZOOM_STEP));
              }}
              disabled={zoomMultiplier <= MIN_ZOOM}
              title="Zoom out"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <div className="text-foreground min-w-14 text-center text-sm tabular-nums">
              {Math.round(zoomMultiplier * 100)}%
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                setZoomMultiplier((current) => clampZoom(current + ZOOM_STEP));
              }}
              disabled={zoomMultiplier >= MAX_ZOOM}
              title="Zoom in"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-muted-foreground text-sm">Loading…</span>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-destructive text-sm">
            Unable to render this PDF.
          </span>
        </div>
      )}
      <div
        ref={containerRef}
        className={cn(
          "min-h-0 flex-1 overflow-auto p-6",
          showToolbar && "border-t border-transparent",
          "[&>*]:mb-4 [&>*:last-child]:mb-0",
          contentClassName,
        )}
      />
    </div>
  );
}
